// src/features/invoice/service/invoiceService.js
import {
  db,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  addDoc,
} from "../../../config/firebase";
import { INVOICE_STATUS } from "../types/invoiceTypes";
import {
  calculateTotals,
  generateInvoiceNumber,
} from "../utils/invoiceHelpers";

class InvoiceService {
  // Collection reference
  getCollection() {
    return collection(db, "invoices");
  }

  // Get all invoices with optional filters
  async getInvoices(filters = {}) {
    try {
      let q = query(this.getCollection(), orderBy("createdAt", "desc"));

      // Apply filters
      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }

      if (filters.clientId && filters.clientId !== "all") {
        q = query(q, where("clientId", "==", filters.clientId));
      }

      if (filters.paymentMethod && filters.paymentMethod !== "all") {
        q = query(q, where("paymentMethod", "==", filters.paymentMethod));
      }

      const querySnapshot = await getDocs(q);
      const invoices = [];
      querySnapshot.forEach((doc) => {
        invoices.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return invoices;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get invoice by ID
  async getInvoiceById(id) {
    try {
      const docRef = doc(db, "invoices", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        };
      } else {
        throw new Error("Invoice not found");
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Create new invoice
  async createInvoice(data) {
    try {
      // Get current count for generating number
      const countSnapshot = await getDocs(this.getCollection());
      const count = countSnapshot.size + 1;

      // Calculate totals
      const totals = calculateTotals(
        data.items,
        data.discount || 0,
        data.discountType || "percentage",
        data.taxRate || 0,
      );

      const invoiceData = {
        ...data,
        ...totals,
        number: generateInvoiceNumber(count),
        status: INVOICE_STATUS.DRAFT,
        paymentStatus: "unpaid",
        amountPaid: 0,
        balanceDue: totals.total,
        isOverdue: false,
        overdueDays: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        statusHistory: [
          {
            status: INVOICE_STATUS.DRAFT,
            date: new Date().toISOString(),
            note: "Invoice created",
          },
        ],
        activities: [
          {
            type: "created",
            date: new Date().toISOString(),
            note: "Invoice created",
          },
        ],
      };

      const docRef = await addDoc(this.getCollection(), invoiceData);
      const newDoc = await getDoc(docRef);

      return {
        id: newDoc.id,
        ...newDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update invoice
  async updateInvoice(id, data) {
    try {
      const docRef = doc(db, "invoices", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Invoice not found");
      }

      let updateData = { ...data };

      // Recalculate totals if items changed
      if (data.items) {
        const totals = calculateTotals(
          data.items,
          data.discount || 0,
          data.discountType || "percentage",
          data.taxRate || 0,
        );
        updateData = { ...updateData, ...totals };
        updateData.balanceDue = totals.total - (docSnap.data().amountPaid || 0);
      }

      updateData.updatedAt = serverTimestamp();

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete invoice
  async deleteInvoice(id) {
    try {
      const docRef = doc(db, "invoices", id);
      await deleteDoc(docRef);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update invoice status
  async updateStatus(id, status) {
    try {
      const docRef = doc(db, "invoices", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Invoice not found");
      }

      const currentData = docSnap.data();
      const statusHistory = currentData.statusHistory || [];

      statusHistory.push({
        status: status,
        date: new Date().toISOString(),
        note: `Status changed to ${status}`,
      });

      const updateData = {
        status: status,
        statusHistory: statusHistory,
        updatedAt: serverTimestamp(),
      };

      // Add specific timestamps based on status
      if (status === INVOICE_STATUS.SENT) {
        updateData.sentAt = serverTimestamp();
      } else if (status === INVOICE_STATUS.VIEWED) {
        updateData.viewedAt = serverTimestamp();
      } else if (status === INVOICE_STATUS.PAID) {
        updateData.paymentDate = serverTimestamp();
        updateData.paymentStatus = "paid";
        updateData.balanceDue = 0;
      } else if (status === INVOICE_STATUS.PARTIALLY_PAID) {
        updateData.paymentStatus = "partial";
      } else if (status === INVOICE_STATUS.OVERDUE) {
        updateData.isOverdue = true;
      }

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Send invoice to client
  async sendInvoice(id) {
    try {
      const docRef = doc(db, "invoices", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Invoice not found");
      }

      const currentData = docSnap.data();
      const statusHistory = currentData.statusHistory || [];

      statusHistory.push({
        status: INVOICE_STATUS.SENT,
        date: new Date().toISOString(),
        note: "Invoice sent to client",
      });

      await updateDoc(docRef, {
        status: INVOICE_STATUS.SENT,
        sentAt: serverTimestamp(),
        statusHistory: statusHistory,
        updatedAt: serverTimestamp(),
      });

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Record payment
  async recordPayment(id, paymentData) {
    try {
      const docRef = doc(db, "invoices", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Invoice not found");
      }

      const currentData = docSnap.data();
      const amountPaid = (currentData.amountPaid || 0) + paymentData.amount;
      const balanceDue = currentData.total - amountPaid;

      const updateData = {
        amountPaid: amountPaid,
        balanceDue: balanceDue,
        updatedAt: serverTimestamp(),
        paymentMethod: paymentData.method || currentData.paymentMethod,
        paymentDate: serverTimestamp(),
      };

      // Update status based on payment
      if (balanceDue <= 0) {
        updateData.status = INVOICE_STATUS.PAID;
        updateData.paymentStatus = "paid";

        const statusHistory = currentData.statusHistory || [];
        statusHistory.push({
          status: INVOICE_STATUS.PAID,
          date: new Date().toISOString(),
          note: `Payment received: ${paymentData.amount}`,
        });
        updateData.statusHistory = statusHistory;
      } else {
        updateData.status = INVOICE_STATUS.PARTIALLY_PAID;
        updateData.paymentStatus = "partial";
      }

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get invoice statistics
  async getInvoiceStats() {
    try {
      const querySnapshot = await getDocs(this.getCollection());
      const invoices = [];
      querySnapshot.forEach((doc) => {
        invoices.push(doc.data());
      });

      const total = invoices.length;
      const statusCounts = {
        draft: 0,
        sent: 0,
        viewed: 0,
        paid: 0,
        partially_paid: 0,
        overdue: 0,
        cancelled: 0,
        refunded: 0,
      };

      let totalValue = 0;
      let totalPaid = 0;
      let overdueCount = 0;

      invoices.forEach((inv) => {
        if (statusCounts[inv.status] !== undefined) {
          statusCounts[inv.status]++;
        }
        totalValue += inv.total || 0;
        totalPaid += inv.amountPaid || 0;
        if (inv.isOverdue || inv.status === "overdue") {
          overdueCount++;
        }
      });

      const paidCount = statusCounts.paid + statusCounts.partially_paid;
      const collectionRate = total > 0 ? (totalPaid / totalValue) * 100 : 0;
      const averageValue = total > 0 ? totalValue / total : 0;

      return {
        total,
        ...statusCounts,
        totalValue: Math.round(totalValue * 100) / 100,
        totalPaid: Math.round(totalPaid * 100) / 100,
        totalOutstanding: Math.round((totalValue - totalPaid) * 100) / 100,
        averageValue: Math.round(averageValue * 100) / 100,
        collectionRate: Math.round(collectionRate * 100) / 100,
        overdueCount: overdueCount,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Handle errors
  handleError(error) {
    console.error("Firebase error:", error);
    return new Error(error.message || "An error occurred");
  }
}

const invoiceService = new InvoiceService();
export default invoiceService;
